/**
 * Displays team info in a clickable card for the team list.
 */

import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { Card, CardBody, Chip } from '@heroui/react';

import type { TeamListItem } from '~/types';

interface TeamCardProps {
  team: TeamListItem;
}

export function TeamCard({ team }: TeamCardProps): JSX.Element {
  const navigate = useNavigate();

  const handleClick = (): void => {
    void navigate(`/org/teams/${team.id}`);
  };

  return (
    <Card
      className="bg-surface hover:bg-surface-elevated"
      isHoverable
      isPressable
      onClick={handleClick}
      data-testid={`team-card-${team.id}`}
    >
      <CardBody>
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-semibold truncate">{team.name}</h3>
          {team.is_default && (
            <Chip color="primary" size="sm">
              Default
            </Chip>
          )}
        </div>

        <p className="text-sm mb-3 min-h-10">
          <span className="font-mono text-xs">{team.slug}</span>
        </p>

        <div className="flex items-center gap-1 text-sm">
          <UserGroupIcon className="w-4 h-4" />
          <span>
            {team.member_count} member{team.member_count !== 1 ? 's' : ''}
          </span>
        </div>
      </CardBody>
    </Card>
  );
}
